import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { Upload, Loader2, RotateCcw, CheckCircle2, Users, Receipt, ArrowLeft } from "lucide-react";

const ENTITIES = [
  { key: "clients", label: "Clienti", icon: Users, desc: "Anagrafiche clienti (nome, contatti, data di nascita, note)." },
  { key: "sales", label: "Storico Vendite", icon: Receipt, desc: "Storico transazioni/vendite. Non genera automazioni." },
];

const STATUS_VARIANT = { committed: "secondary", rolled_back: "destructive", previewed: "outline", draft: "outline" };

/**
 * Wizard di importazione generico ed estendibile (Clienti + Storico Vendite).
 * Supporta auto-detect dell'adapter (gestionale) e mappatura colonne modificabile.
 */
export function ImportWizard({ defaultEntity = "clients", allowEntitySwitch = true, onDone }) {
  const [entity, setEntity] = useState(defaultEntity);
  const [step, setStep] = useState(allowEntitySwitch ? 0 : 1);
  const [busy, setBusy] = useState(false);
  const [batchId, setBatchId] = useState(null);
  const [fields, setFields] = useState([]);
  const [columns, setColumns] = useState([]);
  const [adapters, setAdapters] = useState([]);
  const [adapter, setAdapter] = useState("generic");
  const [mapping, setMapping] = useState({});
  const [preview, setPreview] = useState(null);
  const [dupAction, setDupAction] = useState("ignore");
  const [result, setResult] = useState(null);
  const [batches, setBatches] = useState([]);

  const loadBatches = () => api.get("/import/batches").then((r) => setBatches(r.data)).catch(() => {});
  useEffect(() => { loadBatches(); }, []);

  const reset = () => {
    setStep(allowEntitySwitch ? 0 : 1); setBatchId(null); setFields([]); setColumns([]);
    setAdapters([]); setAdapter("generic"); setMapping({}); setPreview(null);
    setDupAction("ignore"); setResult(null);
  };

  const upload = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    const fd = new FormData(); fd.append("file", file); fd.append("entity", entity); setBusy(true);
    try {
      const { data } = await api.post("/import/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setBatchId(data.batch_id); setColumns(data.columns); setFields(data.fields);
      setAdapters(data.adapters || []); setAdapter(data.detected_adapter || "generic");
      setMapping(data.suggested_mapping || {}); setStep(2);
      const adLabel = (data.adapters || []).find((a) => a.key === data.detected_adapter)?.label;
      toast.success(`${data.total} righe lette${adLabel ? ` · rilevato: ${adLabel}` : ""}`);
    } catch (err) { toast.error(err?.response?.data?.detail || "Errore lettura file"); }
    finally { setBusy(false); e.target.value = ""; }
  };

  const doPreview = async () => {
    setBusy(true);
    try { const { data } = await api.post(`/import/${batchId}/preview`, { mapping, adapter }); setPreview(data); setDupAction("ignore"); setStep(3); }
    catch { toast.error("Errore anteprima"); } finally { setBusy(false); }
  };

  const commit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/import/${batchId}/commit`, { mapping, duplicate_action: dupAction });
      setResult(data); setStep(4); loadBatches(); onDone && onDone();
      toast.success(`Import completato: ${data.created} creati`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore import"); } finally { setBusy(false); }
  };

  const rollback = async (bid) => {
    if (!window.confirm("Annullare questo import? I record importati verranno rimossi.")) return;
    try {
      const { data } = await api.post(`/import/${bid}/rollback`);
      toast.success(`${data.removed} record rimossi`); loadBatches(); onDone && onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore rollback"); }
  };

  const isSales = entity === "sales";

  return (
    <div className="space-y-5" data-testid="import-wizard">
      {/* STEP 0 — scelta entità */}
      {step === 0 && (
        <div className="space-y-4">
          <p className="text-sm text-muted-foreground">Scegli cosa vuoi importare da un altro gestionale. Il sistema riconosce automaticamente il formato (es. Fresha) e ti permette di correggere la mappatura.</p>
          <div className="grid sm:grid-cols-2 gap-3">
            {ENTITIES.map((en) => {
              const Icon = en.icon;
              return (
                <button key={en.key} onClick={() => { setEntity(en.key); setStep(1); }}
                  data-testid={`import-entity-${en.key}`}
                  className="text-left rounded-xl border border-border p-4 hover:border-[hsl(var(--hi-gold))] hover:bg-accent/50 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--hi-gold))]">
                  <Icon className="h-6 w-6 mb-2 text-[hsl(var(--hi-gold))]" />
                  <div className="font-heading text-lg">{en.label}</div>
                  <div className="text-xs text-muted-foreground mt-1">{en.desc}</div>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* STEP 1 — upload */}
      {step === 1 && (
        <div className="space-y-4">
          <div className="flex items-center gap-2">
            {allowEntitySwitch && <Button variant="ghost" size="sm" onClick={() => setStep(0)} data-testid="import-back-entity"><ArrowLeft className="h-4 w-4 mr-1" />Cambia tipo</Button>}
            <Badge variant="outline">{isSales ? "Storico Vendite" : "Clienti"}</Badge>
          </div>
          <p className="text-sm text-muted-foreground">Carica un file <b>CSV</b> o <b>XLSX</b> esportato dal tuo gestionale. Vedrai un'anteprima con simulazione prima di confermare.{isSales ? " Lo storico vendite non invia notifiche automatiche." : ""}</p>
          <Button asChild disabled={busy} data-testid="import-file-button">
            <label className="cursor-pointer w-full justify-center">
              {busy ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Upload className="h-4 w-4 mr-1" />}
              {busy ? "Lettura..." : "Scegli file CSV/XLSX"}
              <input type="file" accept=".csv,.xlsx,.xls" className="hidden" onChange={upload} data-testid="import-file-input" />
            </label>
          </Button>
        </div>
      )}

      {/* STEP 2 — adapter + mapping */}
      {step === 2 && (
        <div className="space-y-3">
          {adapters.length > 0 && (
            <div className="space-y-1">
              <Label className="text-xs">Gestionale di origine (rilevato)</Label>
              <Select value={adapter} onValueChange={setAdapter}>
                <SelectTrigger className="h-9" data-testid="import-adapter"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {adapters.map((a) => <SelectItem key={a.key} value={a.key}>{a.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
          <p className="text-sm text-muted-foreground">Associa le colonne del file ai campi. I suggerimenti sono precompilati e modificabili.</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 max-h-[46vh] overflow-y-auto pr-1">
            {fields.map((f) => (
              <div key={f.key} className="space-y-1">
                <Label className="text-xs">{f.label}</Label>
                <Select value={mapping[f.key] || "none"} onValueChange={(v) => setMapping((m) => ({ ...m, [f.key]: v === "none" ? undefined : v }))}>
                  <SelectTrigger className="h-8 text-xs" data-testid={`map-${f.key}`}><SelectValue placeholder="—" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">— non mappare —</SelectItem>
                    {columns.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            ))}
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setStep(1)}>Indietro</Button>
            <Button onClick={doPreview} disabled={busy} data-testid="import-preview-btn">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Anteprima"}</Button>
          </div>
        </div>
      )}

      {/* STEP 3 — preview + simulazione */}
      {step === 3 && preview && (
        <div className="space-y-3">
          <div className="grid grid-cols-4 gap-2 text-center">
            <div className="rounded-lg bg-muted p-3"><div className="font-heading text-xl">{preview.read}</div><div className="text-[11px] text-muted-foreground">Lette</div></div>
            <div className="rounded-lg bg-[hsl(var(--hi-status-success)/0.12)] p-3"><div className="font-heading text-xl text-[hsl(var(--hi-status-success))]">{preview.valid}</div><div className="text-[11px] text-muted-foreground">{isSales ? "Vendite valide" : "Nuove valide"}</div></div>
            <div className="rounded-lg bg-[hsl(var(--hi-gold-soft))] p-3"><div className="font-heading text-xl text-[hsl(var(--hi-gold))]">{preview.duplicates}</div><div className="text-[11px] text-muted-foreground">Duplicati</div></div>
            <div className="rounded-lg bg-destructive/10 p-3"><div className="font-heading text-xl text-destructive">{preview.errors_count}</div><div className="text-[11px] text-muted-foreground">Errori</div></div>
          </div>

          {isSales && (
            <p className="text-xs text-muted-foreground" data-testid="import-sales-match">Clienti collegati: <b>{preview.clients_matched}</b> · non collegati (verranno registrati come nome libero): <b>{preview.clients_unmatched}</b></p>
          )}

          {isSales && preview.impact && (
            <div className="rounded-lg border border-border p-3 space-y-2" data-testid="import-impact">
              <div className="text-xs font-medium text-muted-foreground">Impatto sulle Analisi (anteprima)</div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-center text-xs">
                <div className="rounded bg-muted p-2"><div className="font-heading text-base">€ {Number(preview.impact.net).toFixed(2)}</div><div className="text-[10px] text-muted-foreground">Fatturato netto</div></div>
                <div className="rounded bg-muted p-2"><div className="font-heading text-base">€ {Number(preview.impact.gross).toFixed(2)}</div><div className="text-[10px] text-muted-foreground">Lordo</div></div>
                <div className="rounded bg-muted p-2"><div className="font-heading text-base">€ {Number(preview.impact.discounts).toFixed(2)}</div><div className="text-[10px] text-muted-foreground">Sconti</div></div>
                <div className="rounded bg-muted p-2"><div className="font-heading text-base">{preview.impact.n_sales}</div><div className="text-[10px] text-muted-foreground">Vendite</div></div>
                <div className="rounded bg-muted p-2"><div className="font-heading text-base">{preview.impact.n_clients}</div><div className="text-[10px] text-muted-foreground">Clienti</div></div>
                <div className="rounded bg-muted p-2"><div className="font-heading text-base">{preview.impact.n_services}</div><div className="text-[10px] text-muted-foreground">Servizi/Voci</div></div>
                <div className="rounded bg-muted p-2"><div className="font-heading text-base">{preview.impact.n_operators}</div><div className="text-[10px] text-muted-foreground">Operatori</div></div>
                <div className="rounded bg-muted p-2"><div className="font-heading text-base text-destructive">{preview.impact.anomalies_count}</div><div className="text-[10px] text-muted-foreground">Anomalie</div></div>
              </div>
              {(preview.impact.period_from || preview.impact.period_to) && (
                <div className="text-[11px] text-muted-foreground">Periodo coperto: <b>{preview.impact.period_from || "?"}</b> → <b>{preview.impact.period_to || "?"}</b></div>
              )}
              <p className="text-[11px] text-muted-foreground">I dati importati alimenteranno Dashboard, Analisi e Report (distinguibili come "importati"). Nessun movimento di cassa, punti o retribuzione verrà generato.</p>
            </div>
          )}

          {preview.duplicates > 0 && (
            <div className="space-y-1.5">
              <Label className="text-xs">Come gestire i duplicati?</Label>
              <Select value={dupAction} onValueChange={setDupAction}>
                <SelectTrigger data-testid="dup-action"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="ignore">Ignora (salta i duplicati)</SelectItem>
                  {!isSales && <SelectItem value="merge">Unisci (integra i campi mancanti, non sovrascrive)</SelectItem>}
                  <SelectItem value="create">Crea comunque un nuovo record</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Anteprima righe */}
          {!isSales && preview.duplicates_sample?.length > 0 && (
            <div className="max-h-40 overflow-y-auto border border-border rounded-lg">
              <Table><TableHeader><TableRow><TableHead>Riga file</TableHead><TableHead>Già presente come</TableHead></TableRow></TableHeader>
                <TableBody>{preview.duplicates_sample.map((d, i) => <TableRow key={i}><TableCell>{d.first_name} {d.last_name} · {d.email || d.phone}</TableCell><TableCell className="text-muted-foreground">{d._dup_name}</TableCell></TableRow>)}</TableBody>
              </Table>
            </div>
          )}
          {isSales && preview.preview?.length > 0 && (
            <div className="max-h-48 overflow-y-auto border border-border rounded-lg">
              <Table><TableHeader><TableRow><TableHead>N.</TableHead><TableHead>Data</TableHead><TableHead>Cliente</TableHead><TableHead>Voci</TableHead><TableHead className="text-right">Totale</TableHead></TableRow></TableHeader>
                <TableBody>{preview.preview.map((s, i) => (
                  <TableRow key={i}>
                    <TableCell className="text-xs">{s.external_id || "—"}</TableCell>
                    <TableCell className="text-xs">{s.sale_date || "—"}</TableCell>
                    <TableCell className="text-xs">{s.client_matched || s.client_name}{s.client_id ? <Badge variant="secondary" className="ml-1 text-[10px]">collegato</Badge> : null}</TableCell>
                    <TableCell className="text-xs text-muted-foreground">{s.lines.map((l) => l.name).join(", ")}</TableCell>
                    <TableCell className="text-right text-xs font-medium">€ {Number(s.total).toFixed(2)}</TableCell>
                  </TableRow>
                ))}</TableBody>
              </Table>
            </div>
          )}

          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setStep(2)}>Indietro</Button>
            <Button onClick={commit} disabled={busy} data-testid="import-commit-btn">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : `Importa ${preview.valid + (dupAction === "create" ? preview.duplicates : 0)} ${isSales ? "vendite" : "clienti"}`}</Button>
          </div>
        </div>
      )}

      {/* STEP 4 — risultato */}
      {step === 4 && result && (
        <div className="text-center py-6 space-y-3">
          <CheckCircle2 className="h-12 w-12 mx-auto text-[hsl(var(--hi-status-success))]" />
          <div className="font-heading text-xl">Import completato</div>
          <p className="text-sm text-muted-foreground">{result.created} creati · {result.merged} uniti · {result.ignored} ignorati · {result.errors} errori</p>
          <Button onClick={reset} data-testid="import-done">Nuovo import</Button>
        </div>
      )}

      {/* Storico import (sempre visibile) */}
      {batches.length > 0 && (
        <div className="pt-4 border-t border-border">
          <div className="text-xs font-medium text-muted-foreground mb-2">Storico import</div>
          <div className="space-y-1.5 max-h-52 overflow-y-auto">
            {batches.map((b) => (
              <div key={b.id} className="flex items-center justify-between text-sm gap-2" data-testid={`import-batch-${b.id}`}>
                <span className="truncate flex items-center gap-1.5">
                  <Badge variant="outline" className="text-[10px]">{b.entity === "sales" ? "Vendite" : "Clienti"}</Badge>
                  <span className="truncate">{b.filename}</span>
                  <Badge variant={STATUS_VARIANT[b.status] || "outline"}>{b.status}</Badge>
                  {b.summary ? <span className="text-muted-foreground">{b.summary.created} creati</span> : null}
                </span>
                {b.status === "committed" && <Button variant="ghost" size="sm" className="h-7 shrink-0" onClick={() => rollback(b.id)} data-testid={`import-rollback-${b.id}`}><RotateCcw className="h-3.5 w-3.5 mr-1" />Annulla</Button>}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
